
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { DollarSign, CreditCard, AlertCircle, CheckCircle } from "lucide-react";
import StudentLayout from "@/components/StudentLayout";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { createApiUrl } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface FeeRecord {
  id: string;
  student_id: string;
  student_name: string;
  amount: number;
  amount_paid: number;
  amount_due: number;
  due_date: string;
  payment_date: string | null;
  status: string;
  payment_status: string;
  payment_mode: string | null;
  payment_comments: string | null;
  receipt_id: string | null;
  cycle_type: string;
  collected_by_name: string | null;
}

const StudentFees = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [feesData, setFeesData] = useState<FeeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeesData();
  }, []);

  const fetchFeesData = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(createApiUrl('/student/fees'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFeesData(data);
      } else if (response.status === 401) {
        localStorage.removeItem('studentData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/login');
      } else {
        throw new Error('Failed to fetch fees data');
      }
    } catch (error) {
      console.error('Fees fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load fees data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading fees data...</p>
          </div>
        </div>
      </StudentLayout>
    );
  }

  const calculateTotals = () => {
    const totalAmount = feesData.reduce((sum, fee) => sum + fee.amount, 0);
    const totalPaid = feesData.reduce((sum, fee) => sum + fee.amount_paid, 0);
    const totalDue = feesData.reduce((sum, fee) => sum + fee.amount_due, 0);
    const paymentPercentage = totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0;
    
    return { totalAmount, totalPaid, totalDue, paymentPercentage };
  };

  const totals = calculateTotals();

  return (
    <StudentLayout>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Fee Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">View your fee status and payment history</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Fee</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-gray-800">₹{totals.totalAmount.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Total fee amount</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid Amount</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-green-600">₹{totals.totalPaid.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Total amount paid</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Due Amount</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-red-600">₹{totals.totalDue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Remaining balance</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Payment Status</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{totals.paymentPercentage}%</div>
              <p className="text-xs text-muted-foreground">Payment completed</p>
              <Progress value={totals.paymentPercentage} className="h-2 mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* All Fees Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Fee Records</CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Complete list of your fees and their status</p>
          </CardHeader>
          <CardContent>
            {feesData.length > 0 ? (
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs sm:text-sm">Amount</TableHead>
                        <TableHead className="text-xs sm:text-sm">Paid</TableHead>
                        <TableHead className="text-xs sm:text-sm">Due</TableHead>
                        <TableHead className="text-xs sm:text-sm">Due Date</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Payment Date</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden md:table-cell">Cycle</TableHead>
                        <TableHead className="text-xs sm:text-sm">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {feesData.map((fee) => (
                        <TableRow key={fee.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">₹{fee.amount.toLocaleString()}</TableCell>
                          <TableCell className="text-green-600 text-xs sm:text-sm">₹{fee.amount_paid.toLocaleString()}</TableCell>
                          <TableCell className="text-red-600 text-xs sm:text-sm">₹{fee.amount_due.toLocaleString()}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{new Date(fee.due_date).toLocaleDateString()}</TableCell>
                          <TableCell className="text-xs sm:text-sm hidden sm:table-cell">
                            {fee.payment_date ? new Date(fee.payment_date).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {fee.cycle_type}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              fee.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-800' 
                                : fee.payment_status === 'partial'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {fee.payment_status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">No Fee Records</h3>
                <p className="text-gray-500 text-sm sm:text-base">
                  Your fee records will appear here once they are created by your teacher.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </StudentLayout>
  );
};

export default StudentFees;
